import { useCallback } from 'react';
import savedFiltersService, { type SavedFilter } from '@/core/services/SavedFiltersService';
import { useFilterEncode } from './useFilterEncode';
import { useFilterDecode } from './useFilterDecode';

/**
 * Hook for saved filters operations (create, update, delete).
 * 
 * **NOTE:** This hook no longer manages cache or fetching.
 * Use `useSavedFiltersCache` for fetching and caching saved filters.
 * 
 * This hook only provides mutation operations that call the service directly.
 * Cache invalidation is handled by the consumer (e.g., GlobalFilterModal).
 */
export function useSavedFilters() {
  const { encodeFilter, encodeFilterWithResponse } = useFilterEncode();
  const { decodeFilter } = useFilterDecode();

  const createSavedFilter = useCallback(
    async (name: string, filterId: string, filterObj: Record<string, unknown>): Promise<SavedFilter> => {
      return await savedFiltersService.createSavedFilter(name, filterId, filterObj);
    },
    [],
  );

  const updateSavedFilter = useCallback(
    async (
      id: string,
      editingFilterId: string,
      name: string,
      filterObj: Record<string, unknown>,
    ): Promise<void> => {
      await savedFiltersService.updateSavedFilter(id, editingFilterId, name, filterObj);
    },
    [],
  );

  const deleteSavedFilter = useCallback(async (filterId: string): Promise<void> => {
    await savedFiltersService.deleteSavedFilter(filterId);
  }, []);

  return { encodeFilter, encodeFilterWithResponse, decodeFilter, createSavedFilter, updateSavedFilter, deleteSavedFilter };
}
